import { useState } from "react";
import { formatDateOnly, formatMoney, workspaceNoun } from "../public-site/publicSiteUtils";
import { MemberPortalSectionTabs } from "./MemberPortalTabs";

function memberPaymentTypeLabel(type = "", qualificationDisplayLabel) {
  const labels = {
    membership_purchase: "Membership purchase",
    membership_renewal: "Membership renewal",
    membership_balance: "Remaining balance",
    membership_plan_change: "Plan change",
    event_registration: "Class registration",
    reservation: "Booking payment",
    donation: "Donation"
  };
  return labels[type] || qualificationDisplayLabel(type || "Payment");
}

function memberPaymentAmount(record = {}) {
  const amountCents = Number(record.amountCents ?? record.details?.amountCents ?? record.payment?.amountCents ?? 0);
  return formatMoney({
    amountCents,
    currency: record.currency || record.details?.currency || record.payment?.currency || "usd"
  });
}

function memberPaymentDate(record = {}) {
  return formatDateOnly(
    record.paidAt || record.createdAt || record.updatedAt || record.occurredAt,
    "Date recorded by the range"
  );
}

function memberPaymentDescription(record = {}) {
  return [
    record.planName,
    record.membershipPlanName,
    record.membershipTypeName,
    record.eventTitle,
    record.resourceName,
    record.description
  ].find(Boolean) || "";
}

function memberPaymentReceiptUrl(record = {}) {
  return record.receiptUrl
    || record.hostedReceiptUrl
    || record.receiptPdfUrl
    || record.invoiceUrl
    || record.hostedInvoiceUrl
    || "";
}

export function memberPaymentStatusLabel(status = "") {
  const labels = {
    active: "Active",
    paid: "Paid",
    complete: "Paid",
    succeeded: "Paid",
    trialing: "Trialing",
    past_due: "Past due",
    unpaid: "Unpaid",
    incomplete: "Incomplete",
    incomplete_expired: "Expired before completion",
    no_payment_required: "No payment required",
    not_required: "No payment required",
    checkout_started: "Checkout started",
    payment_pending: "Payment pending",
    pending: "Pending",
    confirmed: "Confirmed",
    waitlisted: "Waitlisted",
    cancelled: "Cancelled",
    refunded: "Refunded",
    refund_pending: "Refund pending",
    needs_review: "Refund review"
  };
  return labels[status] || status || "Recorded";
}

export function MemberPortalPaymentsPanel({
  loading = false,
  member = null,
  memberSubscriptionSummary,
  onPayMembershipBalance,
  payingMembershipBalance = false,
  payments = {},
  qualificationDisplayLabel,
  range = {}
}) {
  const workspace = workspaceNoun(range, "range");
  const workspaceTitle = workspace.charAt(0).toUpperCase() + workspace.slice(1);
  const [paymentsSection, setPaymentsSection] = useState("summary");
  const receipts = payments?.receipts || [];
  const events = payments?.events || [];
  const balanceDueCents = Number(member?.membership?.balanceDueCents || 0);
  const balancePaymentStatus = member?.membership?.balancePaymentStatus || member?.stripe?.balancePaymentStatus || "";
  const balanceDueAt = member?.membership?.balanceDueAt;
  const balanceCheckoutOpen = ["balance_due", "checkout_started"].includes(balancePaymentStatus) && balanceDueCents > 0;
  const balanceCheckoutExpired = balanceDueAt && Date.parse(balanceDueAt) <= Date.now() && balancePaymentStatus !== "paid";
  const canPayMembershipBalance = Boolean(
    member &&
    member.review?.status === "approved" &&
    balanceCheckoutOpen &&
    !balanceCheckoutExpired
  );
  const recentRows = receipts.length > 0
    ? receipts.slice(0, 6).map((receipt) => ({ ...receipt, rowKind: "receipt" }))
    : events.slice(0, 6).map((event) => ({ ...event, rowKind: "event" }));
  const subscriptionCopy = member ? memberSubscriptionSummary(member) : null;

  return (
    <section className="member-portal-payments-panel" id="member-payments">
      <div className="member-portal-dashboard-header">
        <div>
          <span className="public-section-kicker">Payments</span>
          <h2>Payments and receipts</h2>
          <p>Review what is due, what has been paid, and where to go when a payment window needs help from the {workspace}.</p>
        </div>
      </div>

      <MemberPortalSectionTabs
        activeSection={paymentsSection}
        onSelect={setPaymentsSection}
        sections={[
          { id: "summary", label: "Summary", detail: "Due and billing" },
          { id: "receipts", label: "Receipts", detail: "Payment history" },
          { id: "help", label: "Help", detail: "Billing support" }
        ]}
      />

      {paymentsSection === "summary" && (
        <div className="member-portal-status-grid">
          <div>
            <span>Due now</span>
            <strong>{!member ? "No balance available" : balanceDueCents > 0 && balancePaymentStatus !== "paid" ? memberPaymentAmount({ amountCents: balanceDueCents, currency: member?.membershipPlan?.pricing?.currency }) : "Nothing due"}</strong>
            <small>{!member ? "Membership must be connected before balances or dues can be shown." : balanceCheckoutExpired ? `Contact the ${workspace} to reopen the payment window.` : balanceDueAt ? `Due ${formatDateOnly(balanceDueAt)}` : "No current balance due."}</small>
          </div>
          <div>
            <span>Billing</span>
            <strong>{!member ? "Membership not connected" : subscriptionCopy?.statusLabel || memberPaymentStatusLabel(member?.stripe?.paymentStatus || "not_required")}</strong>
            <small>{!member ? `Billing details appear after this account is connected to a ${workspace} membership.` : subscriptionCopy?.nextBillingLabel || `${workspaceTitle}-managed billing`}</small>
          </div>
          <div>
            <span>Receipts</span>
            <strong>{receipts.length}</strong>
            <small>{receipts.length === 1 ? "One receipt is available below." : receipts.length > 1 ? "Recent receipts are available below." : "Receipts will appear here after payment."}</small>
          </div>
        </div>
      )}

      {paymentsSection === "summary" && balanceDueCents > 0 && balancePaymentStatus !== "paid" && (
        <div className="member-portal-payment-due-card">
          <div>
            <strong>{balanceCheckoutExpired ? "Payment window expired" : "Remaining balance"}</strong>
            <span>{memberPaymentAmount({ amountCents: balanceDueCents, currency: member?.membershipPlan?.pricing?.currency })}{balanceDueAt ? ` / due ${formatDateOnly(balanceDueAt)}` : ""}</span>
          </div>
          {canPayMembershipBalance ? (
            <button
              type="button"
              className="public-primary-link"
              disabled={payingMembershipBalance}
              onClick={() => onPayMembershipBalance(member)}
            >
              {payingMembershipBalance ? "Opening checkout..." : "Pay balance"}
            </button>
          ) : (
            <span>{balanceCheckoutExpired ? `Ask the ${workspace} to reopen checkout.` : memberPaymentStatusLabel(balancePaymentStatus || "payment_pending")}</span>
          )}
        </div>
      )}

      {paymentsSection === "receipts" && (
        <div className="member-portal-split-list">
          <div>
            <h3>Recent receipts</h3>
            {loading ? (
              <div className="compact-status-row">Refreshing payments...</div>
            ) : recentRows.length === 0 ? (
              <div className="compact-status-row">No receipts are on this account yet.</div>
            ) : recentRows.map((row) => (
              <article key={row.receiptId || row.paymentEventId || row.createdAt || row.type}>
                <strong>{memberPaymentTypeLabel(row.type || row.eventType, qualificationDisplayLabel)}</strong>
                <span>{memberPaymentAmount(row)} / {memberPaymentStatusLabel(row.status || row.paymentStatus)}</span>
                {memberPaymentDescription(row) && <span>{memberPaymentDescription(row)}</span>}
                <em>{memberPaymentDate(row)}</em>
                {memberPaymentReceiptUrl(row) && (
                  <div className="member-portal-support-actions">
                    <a className="secondary-button" href={memberPaymentReceiptUrl(row)} target="_blank" rel="noreferrer">
                      Open receipt
                    </a>
                  </div>
                )}
              </article>
            ))}
          </div>
        </div>
      )}

      {paymentsSection === "help" && (
        <div className="member-portal-split-list">
          <div>
            <h3>Billing support</h3>
            <article>
              <strong>Need help with a charge?</strong>
              <span>Use the {workspace} contact path for expired windows, refunds, or membership billing questions.</span>
              <em>Contact {workspace}</em>
            </article>
            <article>
              <strong>Plan changes</strong>
              <span>Use Membership for plan changes, renewals, and member actions tied to your current plan.</span>
              <em>Start in Membership</em>
            </article>
          </div>
        </div>
      )}
    </section>
  );
}
